import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import Pagination from '../../../components/common/Pagination';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import CommitteeMembers from './CommitteeMembers';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_TASKS_ADD } from '../../../Routing/routes';
import { formatIstDate, formatIstDateTime } from '../../../utils/helpers/istDate';

// Core committee: read-only view of member complaints and of the tasks
// assigned to the society's managers. Everything comes from
// /api/committee-oversight (committee.oversight); nothing here edits.

const COMPLAINT_STATUSES = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'REOPENED'];
const TASK_STATUSES = ['PENDING', 'ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'WAITING_APPROVAL', 'ON_HOLD', 'COMPLETED', 'CLOSED', 'CANCELLED', 'REJECTED', 'REOPENED'];

const STATUS_STYLE = {
    PENDING: 'tw-bg-blue-100 tw-text-blue-700',
    ASSIGNED: 'tw-bg-indigo-100 tw-text-indigo-700',
    ACCEPTED: 'tw-bg-sky-100 tw-text-sky-700',
    IN_PROGRESS: 'tw-bg-amber-100 tw-text-amber-700',
    WAITING_APPROVAL: 'tw-bg-purple-100 tw-text-purple-700',
    ON_HOLD: 'tw-bg-gray-200 tw-text-gray-700',
    COMPLETED: 'tw-bg-emerald-100 tw-text-emerald-700',
    CLOSED: 'tw-bg-emerald-50 tw-text-emerald-700',
    CANCELLED: 'tw-bg-gray-100 tw-text-gray-500',
    REJECTED: 'tw-bg-rose-100 tw-text-rose-700',
    REOPENED: 'tw-bg-rose-100 tw-text-rose-700',
};
const PRIORITY_STYLE = {
    LOW: 'tw-bg-slate-100 tw-text-slate-600',
    MEDIUM: 'tw-bg-blue-50 tw-text-blue-600',
    HIGH: 'tw-bg-orange-50 tw-text-orange-600',
    URGENT: 'tw-bg-rose-50 tw-text-rose-600',
    CRITICAL: 'tw-bg-rose-50 tw-text-rose-600',
};

const pretty = (v) => String(v || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const personName = (u) => (u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `User #${u.user_id}` : '—');
const fmtDate = (v) => (v ? formatIstDate(v) : '—');
const fmtDateTime = (v) => (v ? formatIstDateTime(v) : '—');

const Chip = ({ value, styles }) => (value ? (
    <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold tw-whitespace-nowrap ${styles[value] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
        {pretty(value)}
    </span>
) : null);

// Uploaded files are stored as server-relative paths; same resolution as ViewComplaint.
const resolveAssetUrl = (p) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    const base = process.env.REACT_APP_NODE_MOBILE_BASE_URL || '';
    return `${base.replace(/\/+$/, '')}${p.startsWith('/') ? '' : '/'}${p}`;
};

const inputClass = 'tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100';

// Right-hand read-only detail panel.
const DetailPanel = ({ title, subtitle, loading, error, onClose, children }) => (
    <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-justify-end" onClick={onClose}>
        <div className="tw-bg-white tw-w-full tw-max-w-xl tw-h-full tw-flex tw-flex-col tw-shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                <div className="tw-min-w-0">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-break-words">{title}</h3>
                    {subtitle && <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{subtitle}</p>}
                </div>
                <button onClick={onClose} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                    <Icon icon="mdi:close" className="tw-text-lg tw-text-gray-400" />
                </button>
            </div>
            <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
                {loading && (
                    <div className="tw-flex tw-justify-center tw-py-12">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                    </div>
                )}
                {!loading && error && <p className="tw-text-sm tw-text-rose-700 tw-bg-rose-50 tw-rounded-lg tw-p-3">{error}</p>}
                {!loading && !error && children}
            </div>
            <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-text-[11px] tw-text-gray-400">
                <Icon icon="mdi:eye-outline" className="tw-inline tw-mr-1" />
                View only. Changes are made by the society admin and managers.
            </div>
        </div>
    </div>
);

const Field = ({ label, children }) => (
    <div>
        <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">{label}</p>
        <div className="tw-text-xs tw-text-gray-800 tw-mt-0.5">{children || '—'}</div>
    </div>
);

const Thread = ({ items, emptyText }) => (
    items?.length ? (
        <ul className="tw-space-y-3">
            {items.map((c) => (
                <li key={c.comment_id || c.task_comment_id || c.created_at} className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                    <div className="tw-flex tw-justify-between tw-gap-2 tw-text-[11px] tw-text-gray-500">
                        <span className="tw-font-medium tw-text-gray-700">{personName(c.author || c.user || c.commenter)}</span>
                        <span>{fmtDateTime(c.created_at)}</span>
                    </div>
                    <p className="tw-text-xs tw-text-gray-700 tw-mt-1 tw-whitespace-pre-wrap tw-break-words">{c.comment_text || c.body}</p>
                </li>
            ))}
        </ul>
    ) : <p className="tw-text-xs tw-text-gray-400">{emptyText}</p>
);

const CommitteeOversight = () => {
    const societyId = selectedSocietyStorer.getSocietyId();
    const [tab, setTab] = useState('members');
    const navigate = useNavigate();
    const { can } = usePermissions();
    // The Secretary (and any committee role with task.create) assigns work to
    // managers from here; the manager picked in the filter is preselected.
    const canAssignTask = can(PERMISSIONS.TASK_CREATE);

    // shared filters
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [managerId, setManagerId] = useState('');
    const [due, setDue] = useState('');
    const [page, setPage] = useState(1);

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [detail, setDetail] = useState(null); // { kind, id }
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Switching tabs starts from a clean slate — the status sets differ.
    const switchTab = (next) => {
        if (next === tab) return;
        setTab(next);
        setStatus('');
        setDue('');
        setRows([]);
        setMeta(null);
    };

    useEffect(() => { setPage(1); }, [tab, status, debouncedSearch, managerId, due]);

    const fetchList = useCallback(async () => {
        if (!societyId || tab === 'members') return;
        setLoading(true);
        setError('');
        try {
            const params = { society_id: societyId, page, limit: 20 };
            if (status) params.status = status;
            if (debouncedSearch) params.search = debouncedSearch;
            const url = tab === 'complaints'
                ? apiEndpoints.COMMITTEE_COMPLAINTS_API
                : apiEndpoints.COMMITTEE_MANAGER_TASKS_API;
            if (tab === 'tasks') {
                if (managerId) params.manager_user_id = managerId;
                if (due) params.due = due;
            }
            const res = await axiosInstance.get(url, { params });
            setRows(Array.isArray(res?.data) ? res.data : []);
            setMeta(res?.meta?.pagination || null);
        } catch (err) {
            setRows([]);
            setMeta(null);
            setError(err?.response?.data?.message || err?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [societyId, tab, page, status, debouncedSearch, managerId, due]);

    useEffect(() => { fetchList(); }, [fetchList]);
    usePageRefresh(fetchList);

    const openDetail = async (kind, id) => {
        setDetail({ kind, id });
        setDetailData(null);
        setDetailError('');
        setDetailLoading(true);
        try {
            const url = (kind === 'complaint' ? apiEndpoints.COMMITTEE_COMPLAINT_DETAIL_API : apiEndpoints.COMMITTEE_MANAGER_TASK_DETAIL_API)
                .replace(':id', id);
            const res = await axiosInstance.get(url, { params: { society_id: societyId } });
            setDetailData(res?.data || null);
        } catch (err) {
            setDetailError(err?.response?.data?.message || err?.message || 'Failed to load details');
        } finally {
            setDetailLoading(false);
        }
    };

    const statusCounts = meta?.status_counts || {};
    const allCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
    const statuses = tab === 'complaints' ? COMPLAINT_STATUSES : TASK_STATUSES.filter(s => statusCounts[s]);
    const managers = meta?.managers || [];
    const totalPages = meta?.totalPages || 1;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-mb-4">
                <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Core Committee</h1>
                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                    Members of the society's core committee
                </p>
            </div>

            {/* Tabs */}
            <div className="tw-flex tw-gap-1 tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-p-1 tw-w-fit tw-mb-4 tw-shadow-sm">
                {/* Member Complaints / Managers' Tasks tabs are hidden for now; their views below are kept. */}
                {[
                    // { key: 'complaints', label: 'Member Complaints', icon: 'mdi:message-alert-outline' },
                    // { key: 'tasks', label: "Managers' Tasks", icon: 'hugeicons:task-01' },
                    { key: 'members', label: 'Committee Members', icon: 'mdi:account-group-outline' },
                ].map(t => (
                    <button
                        key={t.key}
                        onClick={() => switchTab(t.key)}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-transition-colors ${tab === t.key ? 'tw-bg-blue-600 tw-text-white' : 'tw-text-gray-600 hover:tw-bg-gray-50'}`}
                    >
                        <Icon icon={t.icon} className="tw-text-sm" />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'members' && <CommitteeMembers societyId={societyId} />}

            {tab !== 'members' && (<>
            {/* Filters */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
                    <div className="lg:tw-col-span-2">
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Search</label>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={tab === 'complaints' ? 'Complaint no., subject or description' : 'Task no., title or description'}
                            className={inputClass}
                        />
                    </div>
                    {tab === 'tasks' && (
                        <>
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Manager</label>
                                <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className={inputClass}>
                                    <option value="">All managers</option>
                                    {managers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Due</label>
                                <select value={due} onChange={(e) => setDue(e.target.value)} className={inputClass}>
                                    <option value="">Any time</option>
                                    <option value="today">Due today</option>
                                    <option value="overdue">Overdue</option>
                                </select>
                            </div>
                            {canAssignTask && managers.length > 0 && (
                                <div className="tw-flex tw-items-end">
                                    <button
                                        onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_TASKS_ADD}${managerId ? `?assigned_to=${managerId}` : ''}`)}
                                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700"
                                    >
                                        <Icon icon="mdi:clipboard-plus-outline" className="tw-text-base" />
                                        Assign Task
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
                    {[{ key: '', label: 'All', count: allCount }, ...statuses.map(s => ({ key: s, label: pretty(s), count: statusCounts[s] || 0 }))].map(opt => (
                        <button
                            key={opt.key || 'all'}
                            onClick={() => setStatus(opt.key)}
                            className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-transition-colors ${status === opt.key ? 'tw-bg-blue-600 tw-border-blue-600 tw-text-white' : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                        >
                            {opt.label} ({opt.count})
                        </button>
                    ))}
                </div>
            </div>

            {/* List */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-x-auto">
                {loading ? (
                    <div className="tw-flex tw-justify-center tw-py-12">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                    </div>
                ) : error ? (
                    <p className="tw-m-4 tw-text-sm tw-text-rose-700 tw-bg-rose-50 tw-rounded-lg tw-p-3">{error}</p>
                ) : rows.length === 0 ? (
                    <div className="tw-text-center tw-py-12">
                        <Icon icon={tab === 'complaints' ? 'mdi:message-alert-outline' : 'hugeicons:task-01'} className="tw-text-4xl tw-text-gray-300 tw-mx-auto" />
                        <p className="tw-text-sm tw-text-gray-500 tw-mt-2">
                            {tab === 'complaints' ? 'No complaints for these filters.' : managers.length ? 'No tasks assigned to managers for these filters.' : 'This society has no society managers yet.'}
                        </p>
                    </div>
                ) : tab === 'complaints' ? (
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Complaint', 'Raised by', 'Flat', 'Category', 'Priority', 'Status', 'Assigned to', 'Raised on'].map(h => (
                                    <th key={h} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(c => (
                                <tr key={c.complaint_id} onClick={() => openDetail('complaint', c.complaint_id)} className="tw-border-b tw-border-gray-50 hover:tw-bg-blue-50/40 tw-cursor-pointer">
                                    <td className="tw-px-3 tw-py-2.5">
                                        <div className="tw-font-medium tw-text-gray-800">{c.subject}</div>
                                        <div className="tw-text-[10px] tw-text-gray-400">{c.complaint_code}</div>
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{personName(c.raisedBy || c.raised_by)}</td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600 tw-whitespace-nowrap">
                                        {[c.wing?.title || c.flat?.wing?.title, c.flat?.flat_number].filter(Boolean).join(' - ') || '—'}
                                    </td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{c.category?.name || '—'}</td>
                                    <td className="tw-px-3 tw-py-2.5"><Chip value={c.priority} styles={PRIORITY_STYLE} /></td>
                                    <td className="tw-px-3 tw-py-2.5"><Chip value={c.status} styles={STATUS_STYLE} /></td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">{c.assignedTo || c.assigned_to ? personName(c.assignedTo || c.assigned_to) : <span className="tw-text-amber-600">Unassigned</span>}</td>
                                    <td className="tw-px-3 tw-py-2.5 tw-text-gray-500 tw-whitespace-nowrap">{fmtDate(c.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Task', 'Assigned to', 'Priority', 'Status', 'Progress', 'Due', 'Created'].map(h => (
                                    <th key={h} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(t => {
                                const overdue = t.due_date && new Date(t.due_date) < new Date() && !['COMPLETED', 'CLOSED', 'CANCELLED'].includes(t.status);
                                const assignees = (t.assignments || []).map(a => personName(a.user));
                                return (
                                    <tr key={t.task_id} onClick={() => openDetail('task', t.task_id)} className="tw-border-b tw-border-gray-50 hover:tw-bg-blue-50/40 tw-cursor-pointer">
                                        <td className="tw-px-3 tw-py-2.5">
                                            <div className="tw-font-medium tw-text-gray-800">{t.title}</div>
                                            <div className="tw-text-[10px] tw-text-gray-400">{t.task_number}{t.project?.name ? ` · ${t.project.name}` : ''}</div>
                                        </td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-600">
                                            {assignees.length ? assignees.join(', ') : personName(t.assignee)}
                                        </td>
                                        <td className="tw-px-3 tw-py-2.5"><Chip value={t.priority} styles={PRIORITY_STYLE} /></td>
                                        <td className="tw-px-3 tw-py-2.5"><Chip value={t.status} styles={STATUS_STYLE} /></td>
                                        <td className="tw-px-3 tw-py-2.5 tw-w-28">
                                            <div className="tw-h-1.5 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
                                                <div className="tw-h-full tw-bg-blue-500" style={{ width: `${Math.min(100, Number(t.progress_percent) || 0)}%` }} />
                                            </div>
                                            <div className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">{Number(t.progress_percent) || 0}%</div>
                                        </td>
                                        <td className={`tw-px-3 tw-py-2.5 tw-whitespace-nowrap ${overdue ? 'tw-text-rose-600 tw-font-medium' : 'tw-text-gray-600'}`}>
                                            {fmtDate(t.due_date)}{overdue && ' · Overdue'}
                                        </td>
                                        <td className="tw-px-3 tw-py-2.5 tw-text-gray-500 tw-whitespace-nowrap">{fmtDate(t.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {!loading && rows.length > 0 && totalPages > 1 && (
                    <div className="tw-p-4 tw-border-t tw-border-gray-100">
                        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                    </div>
                )}
            </div>
            </>)}

            {/* Complaint detail */}
            {detail?.kind === 'complaint' && (
                <DetailPanel
                    title={detailData?.subject || 'Complaint'}
                    subtitle={detailData?.complaint_code}
                    loading={detailLoading}
                    error={detailError}
                    onClose={() => setDetail(null)}
                >
                    {detailData && (
                        <div className="tw-space-y-5">
                            <div className="tw-flex tw-gap-2">
                                <Chip value={detailData.status} styles={STATUS_STYLE} />
                                <Chip value={detailData.priority} styles={PRIORITY_STYLE} />
                            </div>
                            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                <Field label="Raised by">{personName(detailData.raisedBy || detailData.raised_by)}</Field>
                                <Field label="Flat">{[detailData.wing?.title || detailData.flat?.wing?.title, detailData.flat?.flat_number].filter(Boolean).join(' - ')}</Field>
                                <Field label="Category">{detailData.category?.name}</Field>
                                <Field label="Assigned to">{detailData.assignedTo || detailData.assigned_to ? personName(detailData.assignedTo || detailData.assigned_to) : 'Unassigned'}</Field>
                                <Field label="Raised on">{fmtDateTime(detailData.created_at)}</Field>
                                <Field label="Last updated">{fmtDateTime(detailData.updated_at)}</Field>
                            </div>
                            <Field label="Description">
                                <p className="tw-whitespace-pre-wrap tw-break-words">{detailData.description}</p>
                            </Field>
                            {detailData.attachments?.length > 0 && (
                                <Field label="Attachments">
                                    <ul className="tw-space-y-1">
                                        {detailData.attachments.map(a => (
                                            <li key={a.attachment_id}>
                                                <a href={a.file_url || resolveAssetUrl(a.file_path)} target="_blank" rel="noopener noreferrer" className="tw-text-blue-600 hover:tw-underline">
                                                    <Icon icon="mdi:paperclip" className="tw-inline tw-mr-1" />{a.file_name || 'Attachment'}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </Field>
                            )}
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">Updates</p>
                                <Thread items={detailData.comments} emptyText="No updates yet." />
                            </div>
                        </div>
                    )}
                </DetailPanel>
            )}

            {/* Task detail */}
            {detail?.kind === 'task' && (
                <DetailPanel
                    title={detailData?.title || 'Task'}
                    subtitle={detailData?.task_number}
                    loading={detailLoading}
                    error={detailError}
                    onClose={() => setDetail(null)}
                >
                    {detailData && (
                        <div className="tw-space-y-5">
                            <div className="tw-flex tw-gap-2">
                                <Chip value={detailData.status} styles={STATUS_STYLE} />
                                <Chip value={detailData.priority} styles={PRIORITY_STYLE} />
                            </div>
                            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                <Field label="Assigned to">
                                    {(detailData.assignments || []).length
                                        ? detailData.assignments.map(a => `${personName(a.user)}${a.role && a.role !== 'ASSIGNEE' ? ` (${pretty(a.role)})` : ''}`).join(', ')
                                        : personName(detailData.assignee)}
                                </Field>
                                <Field label="Created by">{personName(detailData.creator)}</Field>
                                <Field label="Start">{fmtDate(detailData.start_date)}</Field>
                                <Field label="Due">{fmtDate(detailData.due_date)}</Field>
                                <Field label="Progress">{Number(detailData.progress_percent) || 0}%</Field>
                                <Field label="Project">{detailData.project?.name}</Field>
                            </div>
                            <Field label="Description">
                                <p className="tw-whitespace-pre-wrap tw-break-words">{detailData.description}</p>
                            </Field>
                            {detailData.checklist?.length > 0 && (
                                <Field label="Checklist">
                                    <ul className="tw-space-y-1">
                                        {detailData.checklist.map(i => (
                                            <li key={i.checklist_item_id} className="tw-flex tw-items-center tw-gap-1.5">
                                                <Icon icon={i.is_done ? 'mdi:checkbox-marked' : 'mdi:checkbox-blank-outline'} className={i.is_done ? 'tw-text-emerald-600' : 'tw-text-gray-400'} />
                                                <span className={i.is_done ? 'tw-line-through tw-text-gray-400' : ''}>{i.title}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Field>
                            )}
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">Comments</p>
                                <Thread items={detailData.comments} emptyText="No comments yet." />
                            </div>
                            <div>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">History</p>
                                {detailData.history?.length ? (
                                    <ul className="tw-space-y-1.5">
                                        {detailData.history.map(h => (
                                            <li key={h.history_id} className="tw-text-[11px] tw-text-gray-600">
                                                <span className="tw-text-gray-400">{fmtDateTime(h.created_at)}</span>
                                                {' · '}{personName(h.actor)}{' · '}{pretty(h.event_type)}
                                                {h.new_value && <> → <span className="tw-font-medium">{pretty(h.new_value)}</span></>}
                                                {h.note && <span className="tw-text-gray-400"> ({h.note})</span>}
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p className="tw-text-xs tw-text-gray-400">No history yet.</p>}
                            </div>
                        </div>
                    )}
                </DetailPanel>
            )}
        </div>
    );
};

export default CommitteeOversight;
